import React, { useState, useRef } from 'react';
import { Editor } from '@toast-ui/react-editor';
import '@toast-ui/editor/dist/toastui-editor.css';
import { KbArticle, KbFolder } from '../types';
import { getFolderPath } from '../utils/getFolderPath';
import {
  ContentArea,
  ContentHeader,
  ContentActions,
  ActionButton,
  EditorContainer,
  EditorTitleInput,
  Breadcrumbs,
} from '../styles';

interface Props {
  article: KbArticle;
  folders: KbFolder[];
  onSave: (title: string, content: string) => void;
  onCancel: () => void;
}

const ArticleEditor: React.FC<Props> = ({ article, folders, onSave, onCancel }) => {
  const [title, setTitle] = useState(article.title);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef<Editor>(null);
  const path = getFolderPath(folders, article.folderId);

  const handleSave = async () => {
    if (!title.trim() || saving) return;
    setSaving(true);
    const md = editorRef.current?.getInstance().getMarkdown() || '';
    await onSave(title, md);
    setSaving(false);
  };

  return (
    <ContentArea>
      <ContentHeader>
        <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
          {path.length > 0 && (
            <Breadcrumbs style={{ marginBottom: 0, marginRight: 8, flexShrink: 0 }}>
              {path.map((name, i) => (
                <span key={i}>{name} / </span>
              ))}
            </Breadcrumbs>
          )}
          <EditorTitleInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Article title..."
          />
        </div>
        <ContentActions>
          <ActionButton onClick={onCancel}>Cancel</ActionButton>
          <ActionButton $primary onClick={handleSave} disabled={saving || !title.trim()}>
            {saving ? 'Saving...' : 'Save'}
          </ActionButton>
        </ContentActions>
      </ContentHeader>
      <EditorContainer>
        <Editor
          ref={editorRef}
          initialValue={article.content}
          previewStyle="vertical"
          initialEditType="markdown"
          height="100%"
          useCommandShortcut={true}
          usageStatistics={false}
        />
      </EditorContainer>
    </ContentArea>
  );
};

export default ArticleEditor;
